import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import {
  ApiResponse_GetAll_QuestionAnswer,
  useGetAllQuestionAnswers,
} from '@/api/endpoints/questionAnswer/useGetAllQuestionAnswers';
import { QuestionAnswerModel } from '@/api/models/ai/questionAnswer';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import QuestionAnswerCreateModal from '@/components/questionAnswer/QuestionAnswerCreateModal';
import QuestionAnswerDeleteModal from '@/components/questionAnswer/QuestionAnswerDeleteModal';
import QuestionAnswerUpdateModal from '@/components/questionAnswer/QuestionAnswerUpdateModal';

const TABLE_HEAD = [
  { id: 'id', label: 'ID', sortable: true, width: '10%' },
  { id: 'question', label: 'Question / Topic', sortable: true, width: '30%' },
  { id: 'answer', label: 'Answer / Guidance', sortable: true, width: '60%' },
];

type Order = 'asc' | 'desc';
type QuestionAnswer = ApiResponse_GetAll_QuestionAnswer[0];

function getComparator<Key extends keyof QuestionAnswer>(
  order: Order,
  orderBy: Key
): (a: QuestionAnswer, b: QuestionAnswer) => number {
  return (a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    if (order === 'asc') {
      return aValue < bValue ? -1 : 1;
    }
    return bValue < aValue ? -1 : 1;
  };
}

// Mock data for testing
const MOCK_QA_ITEMS: ApiResponse_GetAll_QuestionAnswer = [
  {
    id: 1,
    question: 'What are the office hours for the property management team?',
    answer:
      'The property management team is available Monday through Friday from 9:00 AM to 5:00 PM. For after-hours emergencies, please contact the 24/7 emergency line at (555) 123-4567.',
  },
  {
    id: 2,
    question: 'How do I submit a maintenance request?',
    answer:
      'You can submit a maintenance request through the tenant portal, by calling the property management office, or by emailing maintenance@example.com. Please include detailed information about the issue and any relevant photos.',
  },
  {
    id: 3,
    question: 'What is the parking policy for visitors?',
    answer:
      'Visitors can park in designated visitor parking spaces on a first-come, first-served basis. Overnight parking requires a temporary permit from the property management office. Maximum stay is 48 hours.',
  },
  {
    id: 4,
    question: 'Are pets allowed in the building?',
    answer:
      'Yes, pets are allowed with prior approval. A maximum of two pets per unit is permitted. Dogs must be under 50 pounds, and all pets must be registered with the property management office. Additional pet deposit and monthly pet rent apply.',
  },
  {
    id: 5,
    question: 'What is the process for package delivery?',
    answer:
      'Packages are received at the front desk during business hours. Large packages are stored in the package room and can be picked up with valid ID. Residents will receive email notifications when packages arrive.',
  },
  {
    id: 6,
    question: 'How do I access the building amenities?',
    answer:
      'Building amenities are accessible to all residents with their key fob. The fitness center is open 24/7, the pool is open from 7 AM to 10 PM, and the community room can be reserved through the property management office.',
  },
  {
    id: 7,
    question: 'What is the policy for noise complaints?',
    answer:
      'Quiet hours are from 10 PM to 7 AM. For noise complaints, please contact the property management office during business hours or the emergency line after hours. Repeated violations may result in lease termination.',
  },
  {
    id: 8,
    question: 'How do I set up utilities?',
    answer:
      "Utilities must be set up in the tenant's name before move-in. Contact information for utility providers is provided in the move-in packet. A utility account number is required for move-in approval.",
  },
  {
    id: 9,
    question: 'What is the recycling policy?',
    answer:
      'Recycling is mandatory. Separate bins are provided for paper, plastic, glass, and metal. Composting is available for food waste. Bulk items must be scheduled for pickup through the property management office.',
  },
  {
    id: 10,
    question: 'How do I report a security concern?',
    answer:
      'For immediate security concerns, call 911. For non-emergency security issues, contact the property management office or use the security hotline at (555) 987-6543. All incidents should be reported within 24 hours.',
  },
];

const PropertyLocationViewQuestionAnswerListSubpage: React.FC = () => {
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof QuestionAnswer>('question');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isQuestionAnswerCreateModalOpen, setIsQuestionAnswerCreateModalOpen] = useState(false);
  const [isQuestionAnswerDeleteModalOpen, setIsQuestionAnswerDeleteModalOpen] = useState(false);
  const [isQuestionAnswerUpdateModalOpen, setIsQuestionAnswerUpdateModalOpen] = useState(false);
  const [questionAnswerToDelete, setQuestionAnswerToDelete] = useState<QuestionAnswerModel | null>(
    null
  );
  const [questionAnswerToUpdate, setQuestionAnswerToUpdate] = useState<QuestionAnswerModel | null>(
    null
  );
  const { id: propertyLocationId } =
    useParams<ExtractParams<typeof PMPRoutes.propertyLocationViewQuestionAnswerList>>();
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openAddQuestionAnswer') === 'true') {
      setIsQuestionAnswerCreateModalOpen(true);
    }
  }, [location]);

  const getAllQuestionAnswersQuery = useGetAllQuestionAnswers({
    location_id: Number(propertyLocationId),
  });

  // Use mock data for testing
  const mockData = useMemo(() => MOCK_QA_ITEMS, []);

  const handleRequestSort = (property: keyof QuestionAnswer) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = mockData; // Use mock data instead of API data

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        item =>
          item.question?.toLowerCase().includes(filterName.toLowerCase()) ||
          item.answer?.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [mockData, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60%' }}>
          Add frequently asked questions and their answers to help users understand
          property-specific information.
        </Typography>
        <Button
          variant="contained"
          startIcon={<PlusIcon />}
          onClick={() => setIsQuestionAnswerCreateModalOpen(true)}
        >
          Add Q&A Item
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search Q&A items..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof QuestionAnswer)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    width: '50px',
                    bgcolor: theme.palette.secondary[200],
                    borderRight: 'none',
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllQuestionAnswersQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllQuestionAnswersQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No Q&A items found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllQuestionAnswersQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.question}</TableCell>
                    <TableCell>{row.answer}</TableCell>
                    <TableCell>
                      <RowActionsMenu
                        items={[
                          {
                            label: 'Edit',
                            onClick: () => {
                              setQuestionAnswerToUpdate(row);
                              setIsQuestionAnswerUpdateModalOpen(true);
                            },
                          },
                          {
                            label: 'Delete',
                            onClick: () => {
                              setQuestionAnswerToDelete(row);
                              setIsQuestionAnswerDeleteModalOpen(true);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isQuestionAnswerCreateModalOpen && (
        <QuestionAnswerCreateModal
          onClose={() => setIsQuestionAnswerCreateModalOpen(false)}
          propertyLocationId={propertyLocationId}
        />
      )}

      {isQuestionAnswerUpdateModalOpen && !!questionAnswerToUpdate && (
        <QuestionAnswerUpdateModal
          questionAnswer={questionAnswerToUpdate}
          onClose={() => {
            setQuestionAnswerToUpdate(null);
            setIsQuestionAnswerUpdateModalOpen(false);
          }}
          onQuestionAnswerUpdated={() => getAllQuestionAnswersQuery.refetch()}
        />
      )}

      {isQuestionAnswerDeleteModalOpen && !!questionAnswerToDelete && (
        <QuestionAnswerDeleteModal
          questionAnswer={questionAnswerToDelete}
          onClose={() => {
            setQuestionAnswerToDelete(null);
            setIsQuestionAnswerDeleteModalOpen(false);
          }}
          onQuestionAnswerDeleted={() => getAllQuestionAnswersQuery.refetch()}
        />
      )}
    </Stack>
  );
};

export default PropertyLocationViewQuestionAnswerListSubpage;
